import { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../constants/firebaseModular';
import {
  createUserWithEmailAndPassword,
  getRedirectResult,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut,
  //eslint-disable-next-line
  User,
  //eslint-disable-next-line
  UserCredential,
} from 'firebase/auth';
import * as Sentry from '@sentry/browser';

export interface IAuthContext {
  currentUser: User | null | undefined;
  firebaseGetUser: () => User | null | undefined;
  //eslint-disable-next-line
  firebaseLogin: (email: string, password: string) => Promise<UserCredential>;
  //eslint-disable-next-line
  firebaseSignInWithRedirect: () => Promise<UserCredential | void>;
  firebaseSignOut: () => Promise<void>;
  //eslint-disable-next-line
  firebaseSignUp: (email: string, password: string) => Promise<UserCredential>;
}

const AuthContext = createContext<IAuthContext | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>();
  const [loading, setLoading] = useState<boolean>(true);

  function firebaseLogin(email: string, password: string) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function firebaseSignOut() {
    return signOut(auth);
  }

  function firebaseSignUp(email: string, password: string) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function firebaseGetUser() {
    return auth.currentUser;
  }

  // PASSAGE AUTHENTICATION FLOW
  async function firebaseSignInWithRedirect() {
    const passage = new OAuthProvider('oidc.passage');
    passage.addScope('openid');
    passage.addScope('mail');
    passage.addScope('phone');
    await signInWithRedirect(auth, passage).catch((error) => {
      console.error('Error signing in with redirect', error);
      Sentry.captureException(error);
    });
  }

  // SET CURRENT USER AFTER VALID FIREBASE AUTH LOGIN
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user: User | null) => {
      // HANDLE USERS AUTHENTICATING VIA PASSAGE REDIRECT
      const result = await getRedirectResult(auth);
      if (result) {
        user = result.user;
      }
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    firebaseGetUser,
    firebaseLogin,
    firebaseSignInWithRedirect,
    firebaseSignOut,
    firebaseSignUp,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
